import React, { useState, useEffect } from 'react';
import { editRequestsAPI } from '../../services/api';

function EditRequests() {
  const [editRequests, setEditRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchEditRequests();
  }, []);

  const fetchEditRequests = async () => {
    setLoading(true);
    try {
      const response = await editRequestsAPI.getAllEditRequests();
      setEditRequests(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch edit requests');
    } finally {
      setLoading(false);
    }
  };

  const handleRequest = async (requestId, status, adminNotes = '') => {
    try {
      await editRequestsAPI.updateEditRequest(requestId, { status, adminNotes });
      setSuccess(`Edit request ${status} successfully!`);
      setError('');
      await fetchEditRequests();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${status} request`);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200/50 p-6">
        <div className="flex items-center space-x-4 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-red-500 rounded-xl flex items-center justify-center shadow-lg">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </div>
          <div>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-orange-600 bg-clip-text text-transparent">
              Edit Requests
            </h2>
            <p className="text-gray-600 mt-1">Review and manage user edit requests</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl mb-6">
            {success}
          </div>
        )}

        {loading ? (
          <div className="text-center py-8">Loading edit requests...</div>
        ) : editRequests.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No edit requests</h3>
            <p className="text-gray-500">All caught up! No pending edit requests.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {editRequests.map((request) => (
              <div key={request._id} className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {request.lead?.schoolName || 'Unknown Lead'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      Requested by: <span className="font-medium">{request.requestedBy?.username}</span>
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(request.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(request.status)}`}>
                    {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                  </span>
                </div>

                <div className="mb-4">
                  <h4 className="font-medium text-gray-900 mb-2">Reason:</h4>
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                    {request.reason}
                  </p>
                </div>

                {request.proposedChanges && (
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-2">Proposed Changes:</h4>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        {Object.entries(request.proposedChanges).map(([key, value]) => (
                          <div key={key}>
                            <span className="font-medium text-gray-700 capitalize">
                              {key.replace(/([A-Z])/g, ' $1').trim()}:
                            </span>
                            <span className="ml-2 text-gray-900">{value || 'N/A'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {request.adminNotes && (
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-2">Admin Notes:</h4>
                    <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                      {request.adminNotes}
                    </p>
                  </div>
                )}

                {request.status === 'pending' && (
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        const notes = prompt('Add admin notes (optional):');
                        handleRequest(request._id, 'approved', notes || '');
                      }}
                      className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        const notes = prompt('Add rejection reason:');
                        if (notes) {
                          handleRequest(request._id, 'rejected', notes);
                        }
                      }}
                      className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition"
                    >
                      Reject
                    </button>
                  </div>
                )}

                {request.reviewedBy && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500">
                      Reviewed by {request.reviewedBy.username} on{' '}
                      {new Date(request.reviewedAt).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default EditRequests;